import React, { useMemo } from 'react';
import { Select, Skeleton } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { SubAreaSelectProps } from './SubAreaSelect.types';
import { useSubAreasByRegionQuery } from '../../../../application/queries/subAreaQueries';
import { useAuth } from '../../../hooks/useAuth';

/**
 * SubAreaSelect Component
 *
 * Dropdown select component for choosing a SubArea within a Region.
 * Automatically fetches SubAreas for the selected Region.
 *
 * Features:
 * - Fetches SubAreas when regionId is provided
 * - Display format: "SubArea Name (RadiusKm km radius)"
 * - Loading state with skeleton
 * - Disabled when no region selected
 * - Error handling
 *
 * Usage:
 * ```tsx
 * <SubAreaSelect
 *   regionId={selectedRegionId}
 *   value={selectedSubAreaId}
 *   onChange={handleSubAreaChange}
 *   label="Select SubArea"
 *   required
 * />
 * ```
 */
export const SubAreaSelect: React.FC<SubAreaSelectProps> = ({
  regionId,
  value,
  onChange,
  label = 'SubArea',
  description,
  placeholder = 'Select a subarea...',
  error,
  required = false,
  disabled = false
}): React.ReactElement => {
  const { user } = useAuth();

  // Fetch SubAreas for the selected Region
  const { data: subAreas = [], isLoading } = useSubAreasByRegionQuery(
    regionId || '',
    user?.role,
    !!regionId // Only fetch if regionId is provided
  );

  // Convert SubAreas to Select data format
  const selectData = useMemo(() => {
    const activeSubAreas = subAreas.filter((subArea) => subArea.isActive);

    // Prepend "All SubAreas" option if there are multiple SubAreas
    const options = activeSubAreas.map((subArea) => ({
      value: subArea.id,
      label: `${subArea.name} (${subArea.radiusKm} km radius)`
    }));

    // Add "All SubAreas" option at the top if region has SubAreas
    if (activeSubAreas.length > 0) {
      options.unshift({
        value: '__ALL__',
        label: `All SubAreas in this Region (${activeSubAreas.length} total)`
      });
    }

    return options;
  }, [subAreas]);

  // Show skeleton while loading
  if (isLoading && regionId) {
    return <Skeleton height={36} radius="sm" />;
  }

  // Disabled if no region selected
  const isDisabled = disabled || !regionId;

  return (
    <Select
      label={label}
      description={description || (!regionId ? 'Select a region first' : undefined)}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      data={selectData}
      error={error}
      required={required}
      disabled={isDisabled}
      leftSection={<IconMapPin size={16} />}
      searchable
      clearable
      maxDropdownHeight={300}
      nothingFoundMessage={regionId ? 'No subareas found in this region' : 'Select a region first'}
    />
  );
};
